import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, InputNumber, Form, Switch, Tabs } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';
import ImageSelector from '../../components/ImageSelector';
import { useSearchParams, Link } from 'react-router-dom';
import MultiFieldRuleForm from '../../components/FieldRuleForm/MultiFieldRuleForm';
import RichEditor from '../../components/RichEditor';
import FieldMultiValueInput from '../../components/FieldValueInput/FieldMultiValueInput';
import ObjSelector from '../../components/ObjSelector';

const typMap = {
    0: '常规',
    1: '家政体检',
    2: '家政保险',
    3: '家政合同',
    4: '面试记录',
};
const statusMap = {
    0: { text: '显示', type: 'success' },
    1: { text: '隐藏', type: 'danger' },
};

const FormManage = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [formApi, setFormApi] = useState(null);
    const [searchParams] = useSearchParams();
    const typ = parseInt(searchParams.get('typ')) || 0;
    const [showAddRecordModal, setShowAddRecordModal] = useState(false);
    const [addRecordLoading, setAddRecordLoading] = useState(false);
    const [addRecordValues, setAddRecordValues] = useState({});

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/form/list_form/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus,
                    typ,
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/form/add_form/', { ...values, typ });
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/form/edit_form/', {
                ...values,
                uid: currentRecord.uid,
                typ,
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/form/delete_form/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的表单');
            return;
        }
        try {
            const response = await request.post('/go/sp/form/batchedit_form/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '表单名称',
            dataIndex: 'title',
            width: 200,
        },
        {
            title: '图片',
            dataIndex: 'img',
            width: 80,
            render: (text) => text ? <img src={text} alt="img" style={{ width: 40, height: 40, borderRadius: 4 }} /> : '-',
        },
        {
            title: '费用(元)',
            dataIndex: 'price',
            width: 100,
            render: (text) => (text / 100).toFixed(2),
        },
        {
            title: '点击/提交',
            dataIndex: 'click_cnt',
            width: 100,
            render: (text, record) => (
                <Link to={`/FormSuRecords?form_uid=${record.uid}`} style={{ color: '#1890ff', cursor: 'pointer' }}>
                    {text}/{record.record_cnt}
                </Link>
            ),
        },
        {
            title: '最多提交',
            dataIndex: 'max_cnt',
            width: 100,
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 80,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '排序',
            dataIndex: 'sort',
            width: 80,
        },
        {
            title: '上线/下线',
            dataIndex: 'on_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(text)}</div>
                    <div>{formatTimestamp(record.off_time)}</div>
                </div>
            )
        },
        {
            title: '创建/更新时间',
            dataIndex: 'create_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(text)}</div>
                    <div>{formatTimestamp(record.update_time)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 220,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该表单吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                    <Button
                        icon={<IconPlus />}
                        onClick={() => {
                            setCurrentRecord(record);
                            // 解析字段规则
                            let fields = [];
                            try {
                                fields = JSON.parse(record.content)?.fields || [];
                            } catch { fields = []; }
                            // 初始化值
                            const initVals = {};
                            fields.forEach(f => { initVals[f.key] = ''; });
                            setAddRecordValues(initVals);
                            setShowAddRecordModal(true);
                        }}
                    >
                        添加记录
                    </Button>
                </Space>
            )
        }
    ];

    // 1. 新增 FormModal 组件
    const FormModal = ({
        visible,
        onCancel,
        onSubmit,
        currentRecord,
        typ,
        isEdit
    }) => {
        const [formApi, setFormApi] = React.useState(null);
        // 处理初始价格
        const getInitialPrices = () => {
            if (isEdit && currentRecord) {
                try {
                    const content = currentRecord.content ? JSON.parse(currentRecord.content) : {};
                    if (Array.isArray(content.prices) && content.prices.length > 0) {
                        return content.prices.map(item => ({ n: item.n, p: (item.p || 0) / 100 }));
                    } else if (currentRecord.price) {
                        return [{ n: '默认', p: (currentRecord.price || 0) / 100 }];
                    }
                } catch {
                    return [];
                }
            }
            return [];
        };
        const [prices, setPrices] = React.useState(getInitialPrices().length ? getInitialPrices() : [{ n: '', p: 0 }]);
        // 监听 currentRecord 变化，重置 prices
        React.useEffect(() => {
            setPrices(getInitialPrices().length ? getInitialPrices() : [{ n: '', p: 0 }]);
        }, [currentRecord, isEdit]);

        const initialValues = isEdit && currentRecord ? {
            ...currentRecord,
            desc: currentRecord.content ? (() => { try { return JSON.parse(currentRecord.content).desc || ''; } catch { return ''; } })() : '',
            pos: currentRecord.content ? (() => { try { return JSON.parse(currentRecord.content).pos || ''; } catch { return ''; } })() : '',
            submit_title: currentRecord.content ? (() => { try { return JSON.parse(currentRecord.content).submit_title || '提交'; } catch { return '提交'; } })() : '提交',
            success_title: currentRecord.content ? (() => { try { return JSON.parse(currentRecord.content).success_title || '支付成功'; } catch { return '支付成功'; } })() : '支付成功',
            allow_edit: currentRecord.content ? (() => { try { return JSON.parse(currentRecord.content).allow_edit || false; } catch { return false; } })() : false,
            max_user_cnt: currentRecord.content ? (() => { try { return JSON.parse(currentRecord.content).max_user_cnt || 1; } catch { return 1; } })() : 1,
            fields: currentRecord.content ? (() => { try { return JSON.stringify((JSON.parse(currentRecord.content).fields || []), null, 2); } catch { return '[]'; } })() : '[]',
            on_time: !currentRecord.on_time ? null : currentRecord.on_time,
            off_time: !currentRecord.off_time ? null : currentRecord.off_time,
        } : {
            typ,
            title: '',
            img: '',
            click_cnt: 0,
            record_cnt: 0,
            max_cnt: 0,
            on_time: null,
            off_time: null,
            status: 0,
            sort: 0,
            desc: '',
            pos: '',
            submit_title: '提交',
            success_title: '支付成功',
            allow_edit: false,
            max_user_cnt: 1,
            fields: '[]',
        };
        return (
            <Modal
                title={isEdit ? '编辑表单' : '新增表单'}
                visible={visible}
                onCancel={onCancel}
                footer={null}
                centered
                width={700}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                <Form
                    getFormApi={setFormApi}
                    initValues={initialValues}
                    onSubmit={values => {
                        let fieldsArr;
                        try {
                            fieldsArr = JSON.parse(values.fields);
                            if (!Array.isArray(fieldsArr)) throw new Error();
                        } catch {
                            Toast.error('表单字段设置不是合法的JSON数组');
                            return;
                        }
                        const onTime = values.on_time ? Math.floor(new Date(values.on_time).getTime() / 1000) : 0;
                        const offTime = values.off_time ? Math.floor(new Date(values.off_time).getTime() / 1000) : 0;
                        // 价格校验
                        const validPrices = prices.filter(item => !isNaN(item.p) && (prices.length === 1 || item.n));
                        if (!validPrices.length) {
                            Toast.error('请至少设置一个有效价格');
                            return;
                        }
                        const pricesForSave = validPrices.map(item => ({ n: item.n || '', p: Math.round(item.p * 100) }));
                        // price 字段始终为第一个价格
                        let priceField = pricesForSave.length > 0 ? pricesForSave[0].p : 0;
                        const submitValues = {
                            ...values,
                            price: priceField,
                            on_time: onTime,
                            off_time: offTime,
                            content: JSON.stringify({
                                desc: values.desc,
                                pos: values.pos,
                                submit_title: values.submit_title,
                                success_title: values.success_title,
                                allow_edit: values.allow_edit,
                                max_user_cnt: values.max_user_cnt,
                                fields: fieldsArr,
                                prices: pricesForSave,
                            }),
                        };
                        delete submitValues.desc;
                        delete submitValues.pos;
                        delete submitValues.submit_title;
                        delete submitValues.success_title;
                        delete submitValues.allow_edit;
                        delete submitValues.max_user_cnt;
                        delete submitValues.fields;
                        onSubmit(submitValues);
                    }}
                    style={{ padding: '20px' }}
                >
                    <Tabs type="line">
                        <Tabs.TabPane tab="基本信息" itemKey="basic">
                            <Form.Input
                                field="title"
                                label="表单名称"
                                rules={[{ required: true, message: '请输入表单名称' }]}
                            />
                            <Form.Slot label="图片">
                                <ImageSelector
                                    value={formApi?.getValue('img')}
                                    onChange={value => formApi?.setValue('img', value)}
                                    placeholder="请选择图片"
                                    width="100%"
                                    previewSize={100}
                                />
                            </Form.Slot>
                            <Form.InputNumber
                                field="click_cnt"
                                label="点击数"
                                min={0}
                            />
                            <Form.InputNumber
                                field="record_cnt"
                                label="提交数"
                                min={0}
                            />
                            <Form.InputNumber
                                field="max_cnt"
                                label="最多允许提交数"
                                min={0}
                            />
                            <Form.InputNumber
                                field="sort"
                                label="排序"
                                min={0}
                            />
                            <Form.DatePicker
                                field="on_time"
                                label="上线时间"
                                type="dateTime"
                                placeholder="请选择上线时间"
                                style={{ width: '100%' }}
                                format="yyyy-MM-dd HH:mm:ss"
                            />
                            <Form.DatePicker
                                field="off_time"
                                label="下线时间"
                                type="dateTime"
                                placeholder="请选择下线时间"
                                style={{ width: '100%' }}
                                format="yyyy-MM-dd HH:mm:ss"
                            />
                            <Form.Select
                                field="status"
                                label="状态"
                                initValue={0}
                            >
                                <Select.Option value={0}>显示</Select.Option>
                                <Select.Option value={1}>隐藏</Select.Option>
                            </Form.Select>
                            <Form.Slot label="详情描述">
                                <RichEditor
                                    value={formApi?.getValue('desc') || ''}
                                    onChange={value => formApi?.setValue('desc', value)}
                                    placeholder="请输入详情描述"
                                />
                            </Form.Slot>
                            <Form.Input
                                field="pos"
                                label="地点"
                                placeholder="请输入地点"
                            />
                        </Tabs.TabPane>

                        <Tabs.TabPane tab="表单设置" itemKey="settings">
                            {/* 价格设置区域 */}
                            <div style={{ marginBottom: 24 }}>
                                <div style={{ fontWeight: 600, marginBottom: 8 }}>价格(元)</div>
                                {prices.map((item, idx) => (
                                    <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                        {/* 只有一项时不显示名称输入框 */}
                                        {(prices.length > 1) && (
                                            <Input
                                                placeholder="价格名称"
                                                value={item.n}
                                                onChange={v => {
                                                    const arr = prices.slice(); arr[idx].n = v; setPrices(arr);
                                                }}
                                                style={{ width: 120 }}
                                            />
                                        )}
                                        <InputNumber
                                            placeholder="价格(元)"
                                            value={item.p}
                                            min={0}
                                            precision={2}
                                            onChange={v => {
                                                const arr = prices.slice(); arr[idx].p = v; setPrices(arr);
                                            }}
                                            style={{ width: 120 }}
                                        />
                                        <Button
                                            type="danger"
                                            size="small"
                                            onClick={() => setPrices(prices.filter((_, i) => i !== idx))}
                                            disabled={prices.length === 1}
                                        >删除</Button>
                                        {idx === prices.length - 1 && (
                                            <Button
                                                type="primary"
                                                size="small"
                                                onClick={() => setPrices([...prices, { n: '', p: 0 }])}
                                            >添加</Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <Form.Input
                                field="submit_title"
                                label="提交按钮文字"
                                placeholder="请输入提交按钮文字"
                            />
                            <Form.Input
                                field="success_title"
                                label="支付成功后按钮文字"
                                placeholder="请输入支付成功后的提示文字"
                            />
                            <Form.Switch
                                field="allow_edit"
                                label="允许提交后修改"
                            />
                            <Form.InputNumber
                                field="max_user_cnt"
                                label="用户最多提交次数"
                                min={1}
                            />
                        </Tabs.TabPane>

                        <Tabs.TabPane tab="字段设置" itemKey="fields">
                            <Form.Slot label="表单字段设置">
                                <MultiFieldRuleForm
                                    value={(() => { try { return JSON.parse(formApi?.getValue('fields') || '[]'); } catch { return []; } })()}
                                    onChange={fields => formApi?.setValue('fields', JSON.stringify(fields, null, 2))}
                                />
                            </Form.Slot>
                        </Tabs.TabPane>
                    </Tabs>

                    <div style={{ marginTop: 24, textAlign: 'right' }}>
                        <Space>
                            <Button type='tertiary' onClick={onCancel}>取消</Button>
                            <Button type='primary' htmlType="submit">确定</Button>
                        </Space>
                    </div>
                </Form>
            </Modal>
        );
    };

    return (
        <div className={styles.formContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索表单名称"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="表单状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增表单</Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='warning' onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量隐藏</Button>
                            <Button type='primary' onClick={() => handleBatchEdit(0, selectedRowKeys)}>批量显示</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 个表单吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <FormModal
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                onSubmit={handleAdd}
                currentRecord={null}
                typ={typ}
                isEdit={false}
            />
            <FormModal
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                onSubmit={handleEdit}
                currentRecord={currentRecord}
                typ={typ}
                isEdit={true}
            />

            {/* 添加记录弹窗 */}
            <Modal
                title="添加记录"
                visible={showAddRecordModal}
                onCancel={() => setShowAddRecordModal(false)}
                footer={null}
                centered
                width={500}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {currentRecord && (() => {
                    let fields = [];
                    try {
                        fields = JSON.parse(currentRecord.content)?.fields || [];
                    } catch { fields = []; }
                    return (
                        <Form
                            onSubmit={async () => {
                                // 用字段名称n作为k
                                const dataArr = fields.map(f => ({ k: f.n, v: addRecordValues[f.n] }));
                                setAddRecordLoading(true);
                                try {
                                    const res = await request.post('/go/sp/form/add_form_su_record/', {
                                        form_uid: currentRecord.uid,
                                        su_uid: addRecordValues.su_uid || '',
                                        data: JSON.stringify({ r: dataArr }),
                                        status: 0,
                                        sp_remark: addRecordValues.sp_remark || '',
                                    });
                                    if (res.errno === 0) {
                                        Toast.success('添加成功');
                                        setShowAddRecordModal(false);
                                    } else {
                                        Toast.error(res.errstr || '添加失败');
                                    }
                                } catch {
                                    Toast.error('添加失败');
                                } finally {
                                    setAddRecordLoading(false);
                                }
                            }}
                            style={{ padding: '20px 0' }}
                        >
                            <FieldMultiValueInput
                                fields={fields}
                                value={addRecordValues}
                                onChange={setAddRecordValues}
                                nameKey="n"
                            />
                            <hr />
                            {/* 用户ID选择器 */}
                            <div style={{ marginBottom: 16 }}>
                                <label style={{ display: 'block', marginBottom: 8 }}>用户</label>
                                <ObjSelector
                                    value={addRecordValues.su_uid}
                                    onChange={v => setAddRecordValues(vals => ({ ...vals, su_uid: Array.isArray(v) ? v[0] : v }))}
                                    placeholder="请选择用户"
                                    single={true}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <Form.Input
                                field="sp_remark"
                                label="备注"
                                value={addRecordValues.sp_remark}
                                onChange={v => setAddRecordValues(vals => ({ ...vals, sp_remark: v }))}
                            />
                            <div style={{ textAlign: 'right', marginTop: 24 }}>
                                <Space>
                                    <Button onClick={() => setShowAddRecordModal(false)}>取消</Button>
                                    <Button type="primary" htmlType="submit" loading={addRecordLoading}>提交</Button>
                                </Space>
                            </div>
                        </Form>
                    );
                })()}
            </Modal>
        </div>
    );
};

export default FormManage; 